import React, { forwardRef, useImperativeHandle, useState, useId, useRef } from "react";
import i18n from "../../../Services/i18n";
import qfStyles from "./NoteQuickForm.module.css";
import RichEditor from "../../RichEditor/RichEditor";
import DateTimePicker from "../../DateTimePicker/DateTimePicker";
import { addToast } from "../../../Services/toastService";

const NoteQuickForm = forwardRef(({ initialValue }, ref) => {
  const uid = useId();

  const [note, setNote] = useState(initialValue?.note ?? "");
  const [color, setColor] = useState(initialValue?.color ?? "#FFFFFF");
  const [isCritical, setIsCritical] = useState(!!initialValue?.isCritical);
  const [isPinned, setIsPinned] = useState(!!initialValue?.isPinned);
  const [noteDate, setNoteDate] = useState(initialValue?.noteDate ?? null);

  const initialSnapshot = useRef({
    note: initialValue?.note ?? "",
    color: initialValue?.color ?? "#FFFFFF",
    isCritical: !!initialValue?.isCritical,
    isPinned: !!initialValue?.isPinned,
    noteDate: initialValue?.noteDate ?? null,
  });

  const getCurrentState = () => ({
    note,
    color,
    isCritical,
    isPinned,
    noteDate,
  });

  const isDirty = () => {
    const current = getCurrentState();
    const initial = initialSnapshot.current;

    if (current.note !== initial.note) return true;
    if (current.color !== initial.color) return true;
    if (current.isCritical !== initial.isCritical) return true;
    if (current.isPinned !== initial.isPinned) return true;

    const currentDate = current.noteDate ? new Date(current.noteDate).getTime() : null;
    const initialDate = initial.noteDate ? new Date(initial.noteDate).getTime() : null;

    if (currentDate !== initialDate) return true;

    return false;
  };

  useImperativeHandle(ref, () => ({
    validate: () => {
      if (note.length > 4000) {
        addToast(
          i18n.t("ClientForm.Validation.InvalidDescriptionLen"),
          "clientFormValidationEmail",
          "ERROR",
        );
        return false;
      }

      return true;
    },
    getFormData: () => getCurrentState(),
    isDirty,
  }));

  const pastelColors = [
    { hex: "#FFFFFF", key: "White" },
    { hex: "#FFF8E1", key: "Cream" },
    { hex: "#E3F2FD", key: "Sky" },
    { hex: "#E8F5E9", key: "Mint" },
    { hex: "#FCE4EC", key: "Blush" },
    { hex: "#EDE7F6", key: "Lavender" },
    { hex: "#FFF3E0", key: "Sand" },
    { hex: "#F3E5F5", key: "Lilac" },
    { hex: "#E0F7FA", key: "Aqua" },
    { hex: "#F1F8E9", key: "Limewash" },
  ];

  return (
    <div className={qfStyles.container}>
      <div className={qfStyles.controls}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            paddingBottom: "25px",
          }}>
          <label style={{ whiteSpace: "nowrap", fontWeight: "bold" }}>Date</label>
          <div style={{ width: "40%" }}>
            <DateTimePicker
              name="dateServiceOcc"
              value={noteDate}
              onChange={(d) => {
                setNoteDate(d);
              }}
              readOnly={false}
            />
          </div>
        </div>
      </div>

      <RichEditor
        controlled
        value={note}
        onChange={setNote}
        placeholder={""}
        backgroundColor={color}
        height={320}
        showEmoji
        showTextColor
        showFontSize
        showBold
        showItalic
        showUnderline
        showStrikethrough
      />

      <div className={qfStyles.controls}>
        <fieldset className={qfStyles.field}>
          <legend className={qfStyles.label}>{i18n.t("NoteQuickForm.Color")}</legend>

          <div
            className={qfStyles.colorGrid}
            role="radiogroup"
            aria-label={i18n.t("NoteQuickForm.Color")}>
            {pastelColors.map((c) => {
              const id = `${uid}-c-${c.hex.replace("#", "")}`;
              const name = i18n.t(`NoteQuickForm.Colors.${c.key}`);
              const selected = color === c.hex;

              return (
                <label
                  key={c.hex}
                  className={`${qfStyles.colorOption} ${selected ? qfStyles.colorSelected : ""}`}
                  title={name}
                  htmlFor={id}>
                  <input
                    id={id}
                    type="radio"
                    name={`${uid}-color`}
                    value={c.hex}
                    checked={selected}
                    onChange={() => setColor(c.hex)}
                    aria-label={name}
                    className={qfStyles.colorRadio}
                  />
                  <span
                    className={qfStyles.colorBox}
                    style={{ backgroundColor: c.hex }}
                    aria-hidden="true"
                  />
                </label>
              );
            })}
          </div>
        </fieldset>

        <div className={qfStyles.checkGroup}>
          <label className={qfStyles.checkboxItem} htmlFor={`${uid}-critical`}>
            <input
              id={`${uid}-critical`}
              type="checkbox"
              checked={isCritical}
              onChange={(e) => setIsCritical(e.target.checked)}
            />
            <span>{i18n.t("NoteQuickForm.IsCritical")}</span>
          </label>

          <label className={qfStyles.checkboxItem} htmlFor={`${uid}-pinned`}>
            <input
              id={`${uid}-pinned`}
              type="checkbox"
              checked={isPinned}
              onChange={(e) => setIsPinned(e.target.checked)}
            />
            <span>{i18n.t("NoteQuickForm.IsPinned")}</span>
          </label>
        </div>
      </div>

      <textarea name="note" value={note} readOnly hidden />
    </div>
  );
});

export default NoteQuickForm;
